import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UnitService, AuthService } from '../services/api';
import { FaTable, FaBuilding, FaArrowRightFromBracket, FaChevronDown, FaChevronUp } from 'react-icons/fa6';
import logo from '../assets/logo.png';
import './Dashboard.css';
import './UnitDetail.css'; // Réutiliser les styles
import Message from './Message';

const Organization = () => {
    const [organization, setOrganization] = useState({ name: '', email: '' });
    const [initialEmail, setInitialEmail] = useState('');
    const [password, setPassword] = useState({ old_password: '', new_password: '', confirm_password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchOrganization = async () => {
            try {
                const data = await UnitService.getStructureInfo(); // Cet appel retourne aussi name et email
                setOrganization({ name: data.name, email: data.email });
                setInitialEmail(data.email);
            } catch (err) {
                setError('Impossible de charger les informations de l\'organisation.');
            }
        };
        fetchOrganization();
    }, []);

    const handleOrgChange = (e) => {
        const { name, value } = e.target;
        setOrganization(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPassword(prev => ({ ...prev, [name]: value }));
    };

    // Changer l'adresse de connexion ou le mot de passe exige le mot de passe actuel (l'API le vérifie).
    const emailChanged = organization.email.trim() !== initialEmail;
    const needsCurrentPassword = emailChanged || Boolean(password.new_password);

    const errorMessage = (err) => {
        const code = err.response?.data?.error;
        if (code === 'Invalid old password') return 'Mot de passe actuel incorrect.';
        if (code === 'Email already used') return 'Cette adresse e-mail est déjà utilisée par un autre compte.';
        if (code === 'Invalid email') return 'Adresse e-mail invalide.';
        return code || 'Une erreur est survenue.';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (password.new_password && password.new_password !== password.confirm_password) {
            setError('Les nouveaux mots de passe ne correspondent pas.');
            return;
        }
        if (needsCurrentPassword && !password.old_password) {
            setError('Saisissez votre mot de passe actuel pour modifier votre adresse e-mail ou votre mot de passe.');
            return;
        }

        const payload = { name: organization.name, email: organization.email.trim() };
        if (needsCurrentPassword) payload.old_password = password.old_password;
        if (password.new_password) payload.new_password = password.new_password;

        setLoading(true);
        try {
            const result = await UnitService.updateStructure(payload);
            // Identifiants changés : l'API coupe les autres sessions et renvoie un jeton pour celle-ci.
            if (result?.token) AuthService.setToken(result.token);
            setInitialEmail(payload.email);
            setSuccess(needsCurrentPassword
                ? 'Informations mises à jour. Vos autres sessions ont été déconnectées.'
                : 'Informations mises à jour avec succès.');
            setPassword({ old_password: '', new_password: '', confirm_password: '' });
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div id="dashboard-container">
            <div id="dashboard-layout">
                <aside id="sidebar" className={sidebarOpen ? 'open' : ''}>
                    <div id="sidebar-header">
                        <h1><img src={logo} alt="Fiche Santé Logo" style={{ height: '30px', marginRight: '10px' }} /> Fiche Santé</h1>
                    </div>
                    <nav id="sidebar-nav">
                        <ul>
                            <li><span onClick={() => navigate('/dashboard')}><FaTable /><span>Tableau de bord</span></span></li>
                            <li><span className="active"><FaBuilding /><span>Mon organisation</span></span></li>
                        </ul>
                    </nav>
                </aside>

                <main id="main-content">
                    <header id="header">
                        <button type="button" className="sidebar-toggle" aria-label="Menu" aria-expanded={sidebarOpen} onClick={() => setSidebarOpen(!sidebarOpen)}>
                            ☰
                        </button>
                        <div>
                            <h2>Mon organisation</h2>
                        </div>
                        <div>
                            <button onClick={() => { AuthService.logout(); navigate('/signin'); }} className="logout-button" aria-label="Déconnexion">
                                <FaArrowRightFromBracket />
                                <span>Déconnexion</span>
                            </button>
                        </div>
                    </header>

                    {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}

                    <section id="edit-unit-section">
                        <div id="edit-unit-container">
                            <div id="unit-details-card">
                                {error && <Message>{error}</Message>}
                                {success && <Message type="success">{success}</Message>}
                                <form id="unit-form" onSubmit={handleSubmit}>
                                    <div>
                                        <label htmlFor="name">Nom de l'organisation :</label>
                                        <input type="text" id="name" name="name" value={organization.name} onChange={handleOrgChange} required />
                                    </div>
                                    <div>
                                        <label htmlFor="email">Adresse mail :</label>
                                        <input type="email" id="email" name="email" value={organization.email} onChange={handleOrgChange} required />
                                    </div>
                                    {(needsCurrentPassword || showPassword) && (
                                        <div>
                                            <label htmlFor="old_password">Mot de passe actuel :</label>
                                            <input type="password" id="old_password" name="old_password" autoComplete="current-password" value={password.old_password} onChange={handlePasswordChange} required={needsCurrentPassword} />
                                        </div>
                                    )}

                                    <hr />

                                    <div>
                                        <button type="button" className="section-toggle" aria-expanded={showPassword} onClick={() => setShowPassword(!showPassword)}>
                                            <span>Modifier mon mot de passe</span>
                                            {showPassword ? <FaChevronUp /> : <FaChevronDown />}
                                        </button>
                                        {showPassword && (
                                            <div style={{ marginTop: '1rem' }}>
                                                <div>
                                                    <label htmlFor="new_password">Nouveau mot de passe :</label>
                                                    <input type="password" id="new_password" name="new_password" autoComplete="new-password" value={password.new_password} onChange={handlePasswordChange} />
                                                </div>
                                                <div style={{ marginTop: '1rem' }}>
                                                    <label htmlFor="confirm_password">Confirmation du nouveau mot de passe :</label>
                                                    <input type="password" id="confirm_password" name="confirm_password" autoComplete="new-password" value={password.confirm_password} onChange={handlePasswordChange} />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-buttons">
                                        <button type="submit" disabled={loading} className="btn-primary">
                                            {loading ? 'Sauvegarde...' : 'Sauvegarder'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
};

export default Organization;